import { STAY_DAY_ZONE } from "@vidimo/shared";
import { MessageCircle, Phone, PhoneOff } from "lucide-react";
import { useTranslations } from "use-intl";
import { CallActionButton } from "@/components/communications/call-action-button";
import { PersonAvatar } from "@/components/person-avatar";
import { ReservationStatusPill } from "@/components/reservations/reservation-status-pill";
import { type InterfaceFormat, useInterfaceFormat } from "@/i18n/format";
import { transportLabel } from "@/lib/guest-transports";
import { useAllProperties } from "@/lib/hooks/use-all-properties";
import { useCallerLookup } from "@/lib/hooks/use-caller-lookup";
import type { ParsedCaller } from "@/lib/twilio/caller";
import type { DevCallContext } from "@/lib/twilio/voice-context";
import {
  cn,
  getGuestDisplayName,
  getInitialsFromDisplayName,
  getReservationStatusLabel,
} from "@/lib/utils";

/**
 * WhatsApp-style incoming-call screen.
 *
 * Layout:
 *   ┌─────────────────────────────┐
 *   │   INCOMING CALL · WhatsApp  │
 *   │                             │
 *   │         (◯ avatar ◯)        │  ← two animate-ping rings
 *   │                             │
 *   │        Caller Name          │
 *   │       Property · Channel    │
 *   │     [Room 101 · Checked In] │  ← optional pill
 *   │                             │
 *   │                             │
 *   │   ⛌ Decline      ☎ Accept   │
 *   └─────────────────────────────┘
 *
 * Shown when `pendingInvite` is set. On iOS this page is rarely seen —
 * CallKit handles the invite natively — but it's the primary UI on
 * Android, web, and the iOS call-waiting case (Twilio iOS FAQ).
 */

export interface IncomingCallViewProps {
  /** The caller, already parsed from the raw Twilio invite. See `parseCaller`. */
  caller: ParsedCaller;
  /** Dev-only synthetic context. Lets the simulator render the rich UI
   *  without depending on seed data having synced down. */
  dev?: DevCallContext;
  onAccept: () => void;
  onReject: () => void;
}

export function IncomingCallView({
  caller,
  dev,
  onAccept,
  onReject,
}: IncomingCallViewProps) {
  const t = useTranslations("communications.incomingCallView");
  const tLabels = useTranslations("labels");
  const format = useInterfaceFormat();
  const calledProperty = useAllProperties().find(
    (p) => p.id === caller.propertyId
  );
  const { guest, reservation, unitCode } = useCallerLookup(
    caller.e164,
    caller.propertyId
  );

  const isWhatsApp = caller.transport === "whatsapp";
  const transportName = transportLabel(tLabels, caller.transport);
  const TransportIcon = isWhatsApp ? MessageCircle : Phone;

  const callerNumber = caller.displayNumber;

  // Heading: real name when the lookup matched, "Unknown caller" otherwise.
  // The phone number always renders below the heading so staff have it
  // even when they recognise the name.
  const displayName = guest ? getGuestDisplayName(guest) : t("unknownCaller");
  const initials = guest ? getInitialsFromDisplayName(displayName) : "";
  // Reservation can be backed by the live lookup OR by the dev simulator's
  // mock context — the latter lets the rich UI render without depending on
  // whether seed data has synced down yet.
  const reservationStatus = reservation?.status ?? dev?.reservationStatus;
  const effectiveUnitCode = unitCode ?? dev?.unitCode ?? null;
  // Compose a short reservation hint ("Checking in tomorrow" / "Today").
  const reservationHint = reservation
    ? formatReservationHint(
        t,
        format,
        reservation.checkInAt,
        reservation.checkOutAt
      )
    : null;

  return (
    <div
      className={cn(
        "flex h-full flex-col",
        "bg-gradient-to-b from-primary/10 via-background to-background",
        "px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+2rem)]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        <TransportIcon aria-hidden="true" className="h-3.5 w-3.5" />
        <p className="font-medium text-xs uppercase tracking-[0.18em]">
          {isWhatsApp ? t("headerWhatsApp") : t("headerPhone")}
        </p>
      </div>

      {/* Avatar + name */}
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        <div className="relative flex h-32 w-32 items-center justify-center">
          {/* Two staggered pings produce the WhatsApp-style outward wave. */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-primary/30 motion-safe:animate-ping"
          />
          <span
            aria-hidden="true"
            className="absolute inset-2 rounded-full bg-primary/20 [animation-delay:600ms] motion-safe:animate-ping"
          />
          <PersonAvatar
            alt={displayName}
            className="relative z-10 shadow-xl"
            initials={initials}
            reservationStatus={reservation?.status}
            size="2xl"
          />
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="font-semibold text-3xl text-foreground">
            {displayName}
          </h1>
          <p className="text-muted-foreground text-sm">
            {[callerNumber, transportName, calledProperty?.name]
              .filter(Boolean)
              .join(" · ")}
          </p>

          {reservationStatus && effectiveUnitCode ? (
            <ReservationStatusPill
              className="mt-2"
              size="md"
              status={reservationStatus}
            >
              {reservationHint
                ? t("roomStatusWithHint", {
                    hint: reservationHint,
                    status: getReservationStatusLabel(
                      tLabels,
                      reservationStatus
                    ),
                    unit: effectiveUnitCode,
                  })
                : t("roomStatus", {
                    status: getReservationStatusLabel(
                      tLabels,
                      reservationStatus
                    ),
                    unit: effectiveUnitCode,
                  })}
            </ReservationStatusPill>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-around gap-6">
        <CallActionButton
          icon={<PhoneOff className="size-6" />}
          label={t("decline")}
          onClick={onReject}
          testId="incoming-call-decline"
          variant="destructive"
        />
        <CallActionButton
          icon={<Phone className="size-6" />}
          label={t("accept")}
          onClick={onAccept}
          testId="incoming-call-accept"
          variant="success"
        />
      </div>
    </div>
  );
}

// Helpers

function formatReservationHint(
  t: ReturnType<typeof useTranslations<"communications.incomingCallView">>,
  format: InterfaceFormat,
  checkInIso: string | null,
  checkOutIso: string | null
): string | null {
  const checkIn = checkInIso ? new Date(checkInIso) : null;
  const checkOut = checkOutIso ? new Date(checkOutIso) : null;
  const now = new Date();
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = (a: Date, b: Date) =>
    Math.round(
      (startOfDay(a).getTime() - startOfDay(b).getTime()) / 86_400_000
    );

  if (checkIn) {
    const days = dayDiff(checkIn, now);
    if (days === 0) {
      return t("hintCheckingInToday");
    }
    if (days === 1) {
      return t("hintCheckingInTomorrow");
    }
    if (days > 1 && days < 7) {
      return t("hintInDays", { count: days });
    }
  }
  if (checkOut) {
    const days = dayDiff(checkOut, now);
    if (days === 0) {
      return t("hintCheckingOutToday");
    }
    if (days === 1) {
      return t("hintCheckingOutTomorrow");
    }
  }
  if (checkInIso) {
    return format.date(checkInIso, "shortDate", STAY_DAY_ZONE);
  }
  return null;
}
